import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

export type UserRole = 'admin' | 'formateur' | 'participant' | 'financier';

export interface UserProfile {
  id: string;
  profile_id?: string; // ID dans la table profiles (utilisé pour inscriptions, paiements, etc.)
  email: string;
  prenom: string;
  nom: string;
  telephone1?: string;
  telephone2?: string;
  entreprise?: string;
  adresse?: string;
  ville?: string;
  pays?: string;
  role_id: number;
  role: UserRole;
  is_active: boolean;
  created_at: string;
}

interface AuthContextType {
  user: UserProfile | null;
  profile: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  userRole: UserRole | null;
  signIn: (email: string, password: string) => Promise<UserProfile>;
  signUp: (email: string, password: string, userData: Partial<UserProfile>) => Promise<void>;
  signOut: () => Promise<void>;
  /** Alias de signOut pour compatibilité avec les composants existants */
  logout: () => Promise<void>;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
}

const ROLE_MAP: Record<number, UserRole> = {
  1: 'admin',
  2: 'formateur',
  3: 'participant',
  4: 'financier',
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SUPABASE_FUNCTIONS_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL?.replace('/rest/v1', '') || '';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const mapUser = (raw: Record<string, unknown>): UserProfile => {
    let userRole: UserRole = 'participant';
    if (typeof raw.role === 'string') {
      userRole = raw.role as UserRole;
    } else if (typeof raw.role_id === 'number') {
      userRole = ROLE_MAP[raw.role_id] ?? 'participant';
    }

    return {
      id: raw.id as string,
      profile_id: raw.profile_id as string | undefined,
      email: raw.email as string,
      prenom: (raw.prenom as string) || '',
      nom: (raw.nom as string) || '',
      telephone1: raw.telephone1 as string | undefined,
      telephone2: raw.telephone2 as string | undefined,
      entreprise: raw.entreprise as string | undefined,
      adresse: raw.adresse as string | undefined,
      ville: raw.ville as string | undefined,
      pays: raw.pays as string | undefined,
      role_id: (raw.role_id as number) ?? 3,
      role: userRole,
      is_active: (raw.is_active as boolean) ?? true,
      created_at: raw.created_at as string,
    };
  };

  // Résoudre le profile_id depuis la table profiles via user_id
  const resolveProfileId = async (userId: string): Promise<string | undefined> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      if (error || !data) return undefined;
      return data.id as string;
    } catch {
      return undefined;
    }
  };

  const fetchUserProfile = async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          roles (
            nom
          )
        `)
        .eq('id', userId)
        .maybeSingle();

      if (error || !data) return null;

      const profileId = await resolveProfileId(userId);

      const userData = {
        ...data,
        role: (data.roles as { nom?: string } | null)?.nom || null,
        profile_id: profileId,
      };

      return mapUser(userData as Record<string, unknown>);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedUserId = localStorage.getItem('auth_user_id');
        if (storedUserId) {
          const userProfile = await fetchUserProfile(storedUserId);
          if (userProfile && userProfile.is_active) {
            setUser(userProfile);
            setProfile(userProfile);
          } else {
            localStorage.removeItem('auth_user_id');
          }
        }
      } catch (error) {
        console.error('Erreur initAuth:', error);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
  }, []);

  const signIn = async (email: string, password: string): Promise<UserProfile> => {
    try {
      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/functions/v1/auth-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({ email, password }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de la connexion');
      }

      const rawUser = result.user as Record<string, unknown>;
      const profileId = await resolveProfileId(rawUser.id as string);
      const userProfile = mapUser({ ...rawUser, profile_id: profileId });

      localStorage.setItem('auth_user_id', userProfile.id);
      setUser(userProfile);
      setProfile(userProfile);
      return userProfile;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erreur lors de la connexion');
    }
  };

  const signUp = async (email: string, password: string, userData: Partial<UserProfile>) => {
    try {
      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/functions/v1/auth-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '',
        },
        body: JSON.stringify({
          email,
          password,
          nom: userData.nom || '',
          prenom: userData.prenom || '',
          telephone: userData.telephone1,
          role_id: 3,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erreur lors de l\'inscription');
      }

      const rawUser = result.user as Record<string, unknown>;
      const profileId = await resolveProfileId(rawUser.id as string);
      const userProfile = mapUser({ ...rawUser, profile_id: profileId });

      localStorage.setItem('auth_user_id', userProfile.id);
      setUser(userProfile);
      setProfile(userProfile);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erreur lors de l\'inscription');
    }
  };

  const signOut = async () => {
    localStorage.removeItem('auth_user_id');
    setUser(null);
    setProfile(null);
  };

  const updateProfile = async (data: Partial<UserProfile>) => {
    if (!user) throw new Error('Utilisateur non connecté');

    const { error } = await supabase
      .from('users')
      .update(data)
      .eq('id', user.id);

    if (error) throw new Error('Erreur lors de la mise à jour du profil');

    const updated = await fetchUserProfile(user.id);
    if (updated) {
      setUser(updated);
      setProfile(updated);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      isAuthenticated: !!user,
      userRole: user?.role ?? null,
      signIn,
      signUp,
      signOut,
      logout: signOut,
      updateProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth doit être utilisé dans un AuthProvider');
  return context;
}
